import { useCallback, useState } from 'react'

import { logger } from '@/utils/logger'

const log = logger.scoped('useStoredState')

/** Preserve MSAL's cache and unrelated applications' storage during sign-out. */
export function clearUserStoredState(): void {
  try {
    for (let index = localStorage.length - 1; index >= 0; index--) {
      const key = localStorage.key(index)
      if (key?.startsWith('stagehand.')) localStorage.removeItem(key)
    }
  } catch (error) {
    log.warn('Could not clear user preferences', { error })
  }
}

/**
 * Reads once. `undefined` means "no usable value" — absent, unparseable, or a
 * store that refused to be read at all; the caller's default answers all three.
 */
function read<T>(key: string): T | undefined {
  try {
    const raw = localStorage.getItem(key)
    return raw === null ? undefined : (JSON.parse(raw) as T)
  } catch {
    return undefined
  }
}

/**
 * State that survives a reload, in localStorage.
 *
 * localStorage *throws* rather than returning null under blocked-storage
 * policies (private modes, enterprise cookie blocks, sandboxed webviews), so
 * both directions degrade: an unreadable store falls back to the default, an
 * unwritable one still works for the session. Persistence here is a
 * convenience, never correctness — see ProjectProvider, which makes the same
 * bargain for the selected project.
 *
 * Read once on mount: another tab writing the same key is not worth a storage
 * listener for a UI preference.
 *
 * **Interim home for user preferences.** When a real settings page arrives this
 * lifts into a preferences provider — a typed `UserPreferences` object with its
 * defaults in one place, so the page has something to enumerate. Reusing the
 * same storage keys there means saved values carry over instead of silently
 * resetting. If a *third* caller shows up before that page exists, build the
 * provider rather than adding to the pile: the cost of deferring is N call
 * sites each declaring their own key and default, not this one.
 */
export function useStoredState<T>(
  key: string,
  defaultValue: T,
): [T, (value: T) => void] {
  // ?? not ||: a stored `false` is a real answer, not a missing one.
  const [value, setValue] = useState<T>(() => read<T>(key) ?? defaultValue)

  const store = useCallback(
    (next: T) => {
      setValue(next)
      try {
        localStorage.setItem(key, JSON.stringify(next))
      } catch (error) {
        log.warn('Could not persist state', { key, error })
      }
    },
    [key],
  )

  return [value, store]
}
